import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Tags, Settings, ToggleLeft, MessageCircle, ShieldCheck, ShoppingBag, Tag, Users, Star, Mail, Quote, Instagram, FileText, Inbox, LogOut, ExternalLink, Gem, Wallet, Sparkles } from "lucide-react";
import { useAdminAuth } from "../contexts/AdminAuthContext";

const NAV = [
    { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
    { to: "/admin/products", label: "Products", icon: Package, testId: "admin-nav-products" },
    { to: "/admin/categories", label: "Categories", icon: Tags, testId: "admin-nav-categories" },
    { to: "/admin/coupons", label: "Coupons", icon: Tag, testId: "admin-nav-coupons" },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag, testId: "admin-nav-orders" },
    { to: "/admin/cod-forfeits", label: "COD Forfeits", icon: Wallet, testId: "admin-nav-cod-forfeits" },
    { to: "/admin/leads", label: "Leads", icon: Users, testId: "admin-nav-leads" },
    { to: "/admin/customers", label: "Customers", icon: Gem, testId: "admin-nav-customers" },
    { to: "/admin/reviews", label: "Reviews", icon: Star, testId: "admin-nav-reviews" },
    { to: "/admin/newsletter", label: "Newsletter", icon: Mail, testId: "admin-nav-newsletter" },
    { to: "/admin/testimonials", label: "Testimonials", icon: Quote, testId: "admin-nav-testimonials" },
    { to: "/admin/instagram", label: "Instagram", icon: Instagram, testId: "admin-nav-instagram" },
    { to: "/admin/policy-pages", label: "Policy Pages", icon: FileText, testId: "admin-nav-policy-pages" },
    { to: "/admin/contact-messages", label: "Contact Messages", icon: Inbox, testId: "admin-nav-contact-messages" },
    { to: "/admin/chatbot", label: "Chatbot Q&A", icon: MessageCircle, testId: "admin-nav-chatbot" },
    { to: "/admin/site-settings", label: "Site Settings", icon: Settings, testId: "admin-nav-site-settings" },
    { to: "/admin/loyalty-settings", label: "Loyalty Settings", icon: Sparkles, testId: "admin-nav-loyalty-settings" },
    { to: "/admin/cod-settings", label: "COD Settings", icon: Wallet, testId: "admin-nav-cod-settings" },
    { to: "/admin/toggles", label: "Feature Toggles", icon: ToggleLeft, testId: "admin-nav-toggles" },
    { to: "/admin/whitelist", label: "Admin Whitelist", icon: ShieldCheck, testId: "admin-nav-whitelist" },
];

export default function AdminLayout() {
    const { admin, logout } = useAdminAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-beige flex" data-testid="admin-layout">
            <aside className="w-64 bg-ivory border-r border-inkDark/5 hidden lg:flex flex-col">
                <div className="p-6 border-b border-inkDark/5">
                    <div className="font-display text-2xl text-inkDark">Sukaanyas</div>
                    <div className="label-eyebrow mt-1 text-[10px]">Admin Console</div>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={n.testId}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                                    isActive
                                        ? "bg-skyblue/40 text-inkDark"
                                        : "text-inkDark/80 hover:bg-beige hover:text-inkDark"
                                }`
                            }
                        >
                            <n.icon size={16} />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-inkDark/5">
                    <Link to="/" className="flex items-center gap-2 text-sm text-inkMuted hover:text-skyblueDeep mb-3" target="_blank">
                        <ExternalLink size={14} /> View storefront
                    </Link>
                    <div className="text-sm text-inkDark">{admin?.name}</div>
                    <div className="text-xs text-inkMuted truncate">{admin?.email}</div>
                    <button
                        onClick={handleLogout}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-inkDark/10 text-sm hover:border-skyblueDeep transition-colors"
                        data-testid="admin-logout-button"
                    >
                        <LogOut size={14} /> Log out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-ivory border-b border-inkDark/5 flex items-center justify-between px-4 py-3">
                <div className="font-display text-xl">Sukaanyas Admin</div>
                <button onClick={handleLogout} className="text-sm text-inkDark" data-testid="admin-logout-mobile">
                    <LogOut size={16} />
                </button>
            </div>

            <main className="flex-1 p-6 lg:p-10 pt-20 lg:pt-10 max-w-full overflow-x-hidden">
                {/* Mobile nav pills */}
                <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar mb-5 -mx-2 px-2">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            className={({ isActive }) =>
                                `whitespace-nowrap px-4 py-2 rounded-full text-sm border ${
                                    isActive ? "bg-skyblue border-skyblue text-inkDark" : "bg-ivory border-inkDark/10 text-inkDark"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </div>
                <Outlet />
            </main>
        </div>
    );
}
